import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import axios from 'axios';
import { PaginationWrapper } from 'organisms/InvoicesTable/InvoicesTable.styles';
import { MobilePageWrapper } from 'templates/PageWrapper/PageWrapper';
import loaderGif from 'assets/images/loaderGif.gif';
import { useBaoSelector } from 'utils/hooks/redux';
import { SUPER_ADMIN } from 'utils/constants/roles';
import { useWindowDimensions } from 'pages/AuthPage/AuthPage';
import { MobileInvoicesTitle } from 'utils/styles/Typography.styles';
import { MobileCloudOpsReports } from 'molecules/MobileCloudOpsReports/MobileCloudOpsReports';
import { MobileCloudOpsDetail } from 'molecules/MobileCloudOpsReports/MobileCloudOpsDetail';
import { ReportData, Report, Company, IReportParams } from '../types';
import { months } from 'utils/constants/common';
import { StyledCellText } from 'molecules/Table/styles/Text.styles';
import Table from 'molecules/Table';
import {
  getCustomerInvoiceColor,
  getCustomerInvoiceInfo,
  renderRowDetails,
} from 'organisms/CloudOpsReportAdmin/common';
import PaginationActions from 'molecules/Table/TablePagination/Actions';
import { StyledTableContainer } from 'molecules/Table/styles';
import { StyledTablePagination } from 'molecules/Table/styles/Pagination.styles';
import { StyledFilterWrapper } from 'molecules/Filter/Filter.styles';
import ButtonMonthRangePicker from 'molecules/Calendar/ButtonMonthRangePicker';
import dayjs from 'dayjs';
import FilterDetails from 'molecules/Filter/FilterDetails';
import InvoicePreview from 'molecules/InvoicePreview';
import { FilterAutocomplete } from 'atoms/TableFilter';
import { debounce } from 'lodash-es';
import Modal from 'molecules/Modal';
import { ModalContent } from 'molecules/Modal/Modal.styles';
import TimeLogTable from 'molecules/TimeLogTable';
import AdjustmentTable from 'molecules/AdjustmentTable';

const CloudOpsReport = () => {
  const { width } = useWindowDimensions();

  const [reports, setReports] = useState<ReportData>();
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [loader, setLoader] = useState(true);
  const [link, setLink] = useState<string>('');
  const [company, setCompany] = useState<Company>({
    address: '',
    name: '',
    email: '',
    domain: '',
    gcpBillingId: '',
    companyHsId: '',
    lifetime_billings: 0,
    since: '',
    past_due: '',
    auto_pay: false,
    total_due: 0,
    discount: 0,
    createSoftwareDevInvoices: false,
    currency: '',
  });
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openAdjustmentModal, setOpenAdjustmentModal] =
    useState<boolean>(false);
  const [activeReport, setActiveReport] = useState<number>();
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [monthRange, setMonthRange] = useState<[null | Date, null | Date]>([
    null,
    null,
  ]);
  const [invoicePreview, setInvoicePreview] = useState<string | null>(null);
  const [search, setSearch] = useState<string | undefined>('');

  const { value: user } = useBaoSelector((state) => state.user);
  const { currentCompany } = useBaoSelector((state) => state.common);
  const hsId = currentCompany?.company_hs_id
    ? String(currentCompany?.company_hs_id)
    : null;

  const isSuperAdmin = user.userType === SUPER_ADMIN;

  const milisecondsToHours = (miliseconds: number) =>
    miliseconds / 1000 / 60 / 60;

  const reportDetails = useMemo(() => {
    const { count } = reports || {};
    return [{ label: 'Count', value: count ?? '0' }];
  }, [reports]);

  useEffect(() => {
    (async () => {
      if (!hsId) return;

      try {
        const params: IReportParams = {
          company_hs_id: hsId,
          limit: rowsPerPage,
          page: currentPage + 1,
        };

        if (monthRange[0] && monthRange[1]) {
          params.date_from = dayjs(monthRange[0]).format('YYYY-MM-DD');
          params.date_to = dayjs(monthRange[1]).format('YYYY-MM-DD');
        }

        if (search) {
          params.search = search;
        }

        const { data } = await axios.get<ReportData>(
          `${process.env.REACT_APP_USER_SERVICE}/projects/reports`,
          { params }
        );
        data.projects_reports = data.projects_reports.map((item) => {
          const monthly_budget_in_hours = item.project.monthly_budget_in_hours
            ? Math.round(item.project.monthly_budget_in_hours * 10) / 10
            : 0;

          let credit_balance = monthly_budget_in_hours
            ? monthly_budget_in_hours - milisecondsToHours(item.time_tracked)
            : 0;
          credit_balance = Math.round(credit_balance * 10) / 10;
          const minimalMonthlyBudget = item.project.minimum_monthly_budget
            ? item.project.minimum_monthly_budget
            : 0;
          const overBudgetHourlyRate = item.project.monthly_budget_in_hours
            ? item.project.monthly_budget_in_hours
            : 0;

          let curent_consumption = item.time_tracked ? item.time_tracked : 0;
          curent_consumption = milisecondsToHours(curent_consumption);

          const overConsumedHrs = curent_consumption - monthly_budget_in_hours;
          // number formats
          item.project.monthly_budget_in_hours = monthly_budget_in_hours;
          item.project.curent_consumption =
            Math.round(curent_consumption * 10) / 10;
          item.project.over_consumed_hrs =
            overConsumedHrs > 0 ? Math.round(overConsumedHrs * 10) / 10 : 0;

          item.project.credit_balance = credit_balance;
          let overBudget = 0;
          if (credit_balance < 0) {
            overBudget = Math.abs(credit_balance) * overBudgetHourlyRate;
          }
          item.project.overBudget = overBudget;
          item.project.total_fee = overBudget + minimalMonthlyBudget;
          return item;
        });
        setReports(data);
        setLoader(false);
      } catch (getReportError) {
        // console.log('Failed to get report!', getReportError);
      }
    })();

    (async () => {
      try {
        if (hsId) {
          const { data } = await axios.get<Company>(
            `${process.env.REACT_APP_USER_SERVICE}/company/${hsId}`
          );

          setCompany(data);
        }
      } catch (getCompanyError) {
        // console.log('Failed to get company!', getCompanyError);
      }
    })();
  }, [hsId, currentPage, rowsPerPage, monthRange, search]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setLoader(true);
    setCurrentPage(newPage);
  };

  const handleMonthRange = (value: [null | Date, null | Date]) => {
    setLoader(true);
    setMonthRange(value);
  };

  const handleChangeRowsPerPage = (option: number) => {
    setLoader(true);
    setCurrentPage(0);
    setRowsPerPage(option);
  };

  const handleRowClick = useCallback(
    (event, row) => {
      if (activeReport === row.id) {
        setActiveReport(0);
        setLink('');
      } else {
        if (row.tasks_link) {
          axios
            .get(
              `${process.env.REACT_APP_USER_SERVICE}/company/${row.project.company_hs_id}`
            )
            .then((d) => {
              if (d.data.disable_access_to_zstream === false || isSuperAdmin) {
                setLink(row.tasks_link);
              }
            });
        }
        setActiveReport(row.id);
      }
    },
    [activeReport]
  );

  /** debounce handler for two types: textfield and autocomplete */
  const debouncedChangeHandler = useCallback(
    debounce((event, value) => {
      setSearch(value || event?.target?.value || '');
    }, 700),
    []
  );

  const onReset = useCallback(() => {
    setSearch('');
  }, []);

  const handleLogsOpen = useCallback(() => {
    setOpenModal(true);
    document.body.setAttribute('id', 'body');
  }, []);

  const handleLogsClose = useCallback(() => {
    setOpenModal(false);
    document.body.setAttribute('id', '');
  }, []);

  const handleAdjustmentOpen = useCallback(() => {
    setOpenAdjustmentModal(true);
    document.body.setAttribute('id', 'body');
  }, []);

  const handleAdjustmentClose = useCallback(() => {
    setOpenAdjustmentModal(false);
    document.body.setAttribute('id', '');
  }, []);

  const handleOpenDetails = useCallback(
    (report: Report) => {
      if (report.tasks_link) {
        axios
          .get(
            `${process.env.REACT_APP_USER_SERVICE}/company/${report.project.company_hs_id}`
          )
          .then((d) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (d.data.disable_access_to_zstream === false || isSuperAdmin) {
              setLink(report.tasks_link);
            }
          });
      }
      setSelectedReportId(report.id);
    },
    [reports]
  );

  const handleCloseDetails = useCallback(() => {
    setSelectedReportId(null);
    setLink('');
  }, [reports]);

  const handleClosePreview = useCallback(() => {
    setInvoicePreview(null);
    document.body.setAttribute('id', '');
  }, []);

  const rowDetailsRenderer = useCallback(
    (row) =>
      renderRowDetails(
        row,
        row.tasks_link,
        handleLogsOpen,
        handleAdjustmentOpen
      ),
    []
  );

  const columns = useMemo(
    () => [
      {
        key: 'month',
        label: 'Month',
        sortable: false,
        width: '20%',
        render: ({ month, year }: Report) => (
          <StyledCellText $textType="bold">
            {months[month - 1]}, {year}
          </StyledCellText>
        ),
      },
      {
        key: 'project',
        label: 'Project Name',
        sortable: false,
        width: '40%',
        render: ({ project }: Report) => (
          <StyledCellText $textType="default">{project.name}</StyledCellText>
        ),
      },
      // {
      //   key: 'contract_type',
      //   label: 'Contract type',
      //   sortable: false,
      //   width: '20%',
      //   render: ({ project }: Report) => {
      //     const { contract_type } = project || {};

      //     return (
      //       <StyledCellText
      //         $textType="default"
      //         $color={contract_type ? neutrals[700] : palette.warning[600]}
      //       >
      //         {contract_type
      //           ? contractTypeMap[contract_type] || contract_type
      //           : 'Not set'}
      //       </StyledCellText>
      //     );
      //   },
      // },
      {
        key: 'invoice_number',
        label: 'Invoice details',
        sortable: false,
        width: '40%',
        render: (report: Report) => (
          <StyledCellText
            $color={getCustomerInvoiceColor(report)}
            sx={{
              display: 'inline',
              // Only apply if invoice_qb_id is exists
              ...(report?.invoice_qb_id
                ? {
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }
                : {}),
            }}
            onClick={(event) => {
              event?.preventDefault();
              event?.stopPropagation();

              if (report.invoice_qb_id) {
                setInvoicePreview(report.invoice_qb_id);
                document.body.setAttribute('id', 'body');
              }
            }}
          >
            {getCustomerInvoiceInfo(report)}
          </StyledCellText>
        ),
      },
    ],
    [activeReport]
  );

  const activeReportData = reports?.projects_reports?.find(
    ({ id }) => id === activeReport
  );

  if (width < 600 && !isSuperAdmin) {
    if (selectedReportId !== null) {
      const selectedReport = reports?.projects_reports.find(
        ({ id }) => id === selectedReportId
      );
      if (selectedReport) {
        return (
          <MobilePageWrapper
            title={
              <MobileInvoicesTitle variant="h2" my={3}>
                CloudOps
              </MobileInvoicesTitle>
            }
          >
            <MobileCloudOpsDetail
              link={link}
              data={selectedReport}
              onBack={handleCloseDetails}
              companyName={company?.name}
            />
          </MobilePageWrapper>
        );
      }
    }

    return (
      <MobilePageWrapper
        title={
          <MobileInvoicesTitle variant="h2" my={3}>
            CloudOps
          </MobileInvoicesTitle>
        }
      >
        {loader ? (
          <Box sx={{ textAlign: 'center', padding: '40px 0' }}>
            <img src={loaderGif} alt="loader" />
          </Box>
        ) : (
          <>
            <MobileCloudOpsReports
              data={reports}
              onDetails={handleOpenDetails}
            />
            <PaginationWrapper
              style={{ borderRadius: '4px', backgroundColor: '#ffffff' }}
            >
              <StyledTablePagination
                labelDisplayedRows={() => null}
                rowsPerPageOptions={[]}
                count={reports?.count || 0}
                rowsPerPage={rowsPerPage}
                page={currentPage}
                onPageChange={handleChangePage}
                ActionsComponent={(props) => (
                  <PaginationActions
                    {...props}
                    labelRowsPerPage="Items per page"
                    rowsPerPageOptions={[20, 50, 100, 200]}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                )}
              />
            </PaginationWrapper>
          </>
        )}
      </MobilePageWrapper>
    );
  }

  const shouldShowReportsDetails = (() =>
    !(reports?.count === 1 || !monthRange[0] || !monthRange[1]))();

  return (
    <>
      <StyledFilterWrapper>
        <Stack direction="row" gap={3}>
          <ButtonMonthRangePicker
            tooltip="Filter by month"
            onChange={handleMonthRange}
          />

          <FilterAutocomplete
            isFreeSolo={!!search}
            value={search}
            options={[]}
            placeholder="Search by: Project name"
            tooltip="Filter by project"
            onChange={debouncedChangeHandler}
            onReset={onReset}
          />
        </Stack>

        <FilterDetails
          show={!loader && shouldShowReportsDetails}
          data={reportDetails}
        />
      </StyledFilterWrapper>
      <StyledTableContainer style={{ minWidth: 800 }}>
        <Table
          idKey="id"
          loading={loader}
          collapsible
          collapsedRow={activeReport}
          columns={columns}
          data={reports?.projects_reports || []}
          onRowClick={handleRowClick}
          rowDetailsRenderer={rowDetailsRenderer}
        />
        <StyledTablePagination
          labelDisplayedRows={() => null}
          rowsPerPageOptions={[]}
          count={reports?.count || 0}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onPageChange={handleChangePage}
          ActionsComponent={(props) => (
            <PaginationActions
              {...props}
              labelRowsPerPage="Items per page"
              rowsPerPageOptions={[20, 50, 100, 200]}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        />
      </StyledTableContainer>

      {invoicePreview && (
        <InvoicePreview id={invoicePreview} onClose={handleClosePreview} />
      )}

      <Modal
        open={openModal}
        maxWidth="lg"
        title={`${
          activeReportData?.month ? months[activeReportData?.month - 1] : ''
        } time logs details`}
        subtitle={`This window provides comprehensive breakdown of time logs for the ${
          activeReportData?.month ? months[activeReportData?.month - 1] : ''
        }. The details include a list of users, their hourly rates, daily time logs with corresponding amounts, and the total logged hours and amount per user for the month. Additionally, you can download a CSV file containing this information for further analysis or record-keeping.`}
        onClose={handleLogsClose}
        sx={{ zIndex: 33333 }}
      >
        <ModalContent
          sx={{
            paddingBottom: '16px !important',
            maxHeight: '400px',
            overflow: 'hidden',
          }}
        >
          {activeReportData ? (
            // eslint-disable-next-line
            <TimeLogTable report={activeReportData as any} />
          ) : (
            '...'
          )}
        </ModalContent>
      </Modal>
      <Modal
        open={openAdjustmentModal}
        maxWidth="md"
        title={`Adjustment time logs details from ${
          activeReportData?.month ? months[activeReportData?.month - 1] : ''
        }`}
        subtitle={
          <Stack>
            <span>
              This window provides difference breakdown between total hours
              invoiced and changes made after invoice creation for the{' '}
              {activeReportData?.month
                ? months[activeReportData?.month - 1]
                : ''}
              .
            </span>
            <span>
              The details include a list of users, their hourly rates, daily
              time logs with corresponding amounts, and the total logged hours
              and amount per user for the month. Additionally, you can download
              a CSV file containing this information for further analysis or
              record-keeping.
            </span>
          </Stack>
        }
        onClose={handleAdjustmentClose}
        sx={{ zIndex: 33333 }}
      >
        <ModalContent
          sx={{
            paddingBottom: '16px !important',
            maxHeight: '400px',
            overflow: 'hidden',
          }}
        >
          {activeReportData ? (
            // eslint-disable-next-line
            <AdjustmentTable report={activeReportData as any} />
          ) : (
            '...'
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default CloudOpsReport;
